import { gql } from '@apollo/client';

import './Story.scss';
import RichStory from '@justgiving/rich-story';
import { captureException } from '@sentry/browser';
import React, { FunctionComponent } from 'react';
import classNames from 'classnames';
import {
  OwnerType,
  StoryFragment,
  StoryType,
} from '../../../types/__generated__';
import clientConfig from '../../client/config';
import usePageQuery, {
  UsePageQueryResult,
  buildQuery,
} from '../../hooks/usePageQuery';
import styles from './Story.scss';
import StorySkeleton from './StorySkeleton';
import { usePageMeta } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { useProduct } from '../../hooks/page/product/useProduct';
import PAGE_TYPES from '../../constants/pageTypes';

const FRAGMENT = buildQuery(gql`
  fragment Story on Page {
    id
    storyWithType {
      type
      content
    }
  }
`);

const Story: FunctionComponent = ({ children }) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const result = usePageQuery<StoryFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  const { isPreview, pageType } = usePageMeta();
  const {
    data: { ownerType },
  } = useProduct();

  if (result.loading) {
    return <StorySkeleton animated displayRefreshButton={false} />;
  }

  if (result.error) {
    captureException(result.error);
    return <StorySkeleton onRefresh={result.refetch} />;
  }

  if (!UsePageQueryResult.hasData(result)) {
    return null;
  }

  let story = result.data.storyWithType;

  if (!story?.content && isPreview) {
    if (
      pageType === PAGE_TYPES.ONE_PAGE &&
      ownerType === OwnerType.Organisation
    ) {
      story = {
        content: t('campaign.preview.StoryPlaceholder'),
        type: StoryType.Plaintext,
      };
    }
  }

  if (!story?.content) {
    return <>{children}</>;
  }

  if (story.type === StoryType.RichStoryV1Json) {
    return (
      <div className={styles.story}>
        <RichStory
          imageBaseUri={clientConfig.imageServiceUrl}
          imageTemplate="CrowdfundingStoryImage"
          storyNodes={JSON.parse(story.content)}
        />
      </div>
    );
  }

  if (story.type === StoryType.Html) {
    return (
      <div
        data-qa="story-content"
        className={classNames(
          'jg-pages-section__content',
          'jg-space-plmd@xl',
          styles.story,
        )}
        dangerouslySetInnerHTML={{ __html: story.content }}
      />
    );
  }

  if (story.type === StoryType.Plaintext) {
    return (
      <div
        data-qa="story-content"
        className={classNames(
          'jg-pages-section__content',
          'jg-space-plmd@xl',
          styles.story,
        )}
      >
        {story.content}
      </div>
    );
  }

  return <>{children}</>;
};

export default Story;
